import { useMutation, useQueryClient } from "@tanstack/react-query";

import { mutateData } from "@/core/api/api-methods";
import {
  apiErrorHandler,
  getBackendErrorMessage,
} from "@/core/api/api-error-handler";
import {
  invalidateAfterMutation,
  kycCaseCacheKeys,
} from "@/core/api/invalidate-after-mutation";
import { logger } from "@/core/utils/func/logger";
import { kycEndpoints } from "@/features/kyc/api/endpoints";
import type { KycCaseDto } from "@/features/kyc/api/dto";

/**
 * Soumet un dossier KYC à la revue (`POST /KycCases/{id}/submit`) :
 * DRAFT → SUBMITTED. Échoue côté serveur si aucune pièce n'a été confirmée
 * ou si le dossier n'est plus en DRAFT.
 */
export const useSubmitKycCase = (kycCaseId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["kyc", "kyc-cases", "submit", kycCaseId],
    mutationFn: () =>
      mutateData({
        url: kycEndpoints.kycCases.submit(kycCaseId),
        method: "POST",
      }) as Promise<KycCaseDto>,
    onSuccess: () => {
      return invalidateAfterMutation(queryClient, kycCaseCacheKeys(kycCaseId));
    },
    onError: (error) => {
      const message = getBackendErrorMessage(error) ?? apiErrorHandler(error);
      logger.error(
        `useSubmitKycCase failed: ${message}`,
        error instanceof Error ? error : undefined,
        { prefix: "kyc" }
      );
    },
  });
};
